import React, { useMemo, useState } from "react";
import { Box, Button, Sheet, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import { AddIcon } from "static/image/common/IconAssets";
import { type TaskEditorState } from "./utils/taskEditorUtils";
import { buildTaskEditorListSections } from "./utils/taskEditorListUtils";
import {
  useTaskEditorListDrag,
  type ListDropTarget,
} from "./utils/useTaskEditorListDrag";
import { taskEditorScrollbarSx } from "./utils/taskEditorScrollbarSx";
import { UpArrowSvg } from "../../static/image/common/SvgAssets";

interface TaskListPanelProps {
  tasks: TaskEditorState[];
  activeTaskId?: string;
  // 백그라운드 포함 Agent 스트리밍 중인 활동지 id — 제목 좌측 진행 인디케이터 표시용.
  streamingTaskIds?: string[];
  onSelectTask: (taskId: string) => void;
  onCreateTask: () => void;
  onEditMeta: (taskId: string) => void;
  // 드래그 1회의 결과. targetSetNumber=9999 는 미분류, beforeTaskId=null 은 섹션 끝.
  onReorder: (
    draggedTaskId: string,
    targetSetNumber: number,
    beforeTaskId: string | null
  ) => void;
}

const KEY_BADGE_COLOR = "#F59E0B";
const DROP_LINE_COLOR = theme.vars.palette.primary.solid;

// Agent 응답 스트리밍 중인 활동지의 제목 좌측에 띄우는 진행 인디케이터.
// "생각을 다듬는 중" — 세 점이 순차로 일렁이는(opacity wave) 잔잔한 애니메이션.
function StreamingIndicator() {
  const dotSx = (delay: string) => ({
    width: "3px",
    height: "3px",
    borderRadius: "50%",
    backgroundColor: theme.vars.palette.Content.Highlight,
    "@keyframes taskThinking": {
      "0%, 80%, 100%": { opacity: 0.25 },
      "40%": { opacity: 1 },
    },
    animation: "taskThinking 1.2s ease-in-out infinite",
    animationDelay: delay,
  });
  return (
    <Box
      aria-label="Agent 응답 생성 중"
      title="Agent 응답 생성 중"
      sx={{ display: "flex", alignItems: "center", gap: "2px" }}
    >
      <Box sx={dotSx("0s")} />
      <Box sx={dotSx("0.16s")} />
      <Box sx={dotSx("0.32s")} />
    </Box>
  );
}

export default function TaskListPanel({
  tasks,
  activeTaskId,
  streamingTaskIds,
  onSelectTask,
  onCreateTask,
  onEditMeta,
  onReorder,
}: TaskListPanelProps) {
  const sections = useMemo(() => buildTaskEditorListSections(tasks), [tasks]);
  const streamingSet = useMemo(
    () => new Set(streamingTaskIds ?? []),
    [streamingTaskIds]
  );
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set()
  );

  const {
    dragState,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = useTaskEditorListDrag(onReorder);
  const { draggingTaskId, dropTarget } = dragState;

  const toggleSection = (sectionKey: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  // 행 위 포인터 위치(상/하단)로 "앞/뒤 삽입" 결정.
  const buildRowTarget = (
    event: React.DragEvent<HTMLElement>,
    setNumber: number,
    taskId: string,
    nextTaskId: string | null
  ): ListDropTarget => {
    const rect = event.currentTarget.getBoundingClientRect();
    const isAfter = event.clientY > rect.top + rect.height / 2;
    return { setNumber, beforeTaskId: isAfter ? nextTaskId : taskId };
  };

  const isDropLineActive = (setNumber: number, beforeTaskId: string | null) =>
    dropTarget !== null &&
    dropTarget.setNumber === setNumber &&
    dropTarget.beforeTaskId === beforeTaskId;

  return (
    <Sheet
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderTop: 0,
        borderBottom: 0,
        borderLeft: 0,
        borderRadius: 0,
        backgroundColor: theme.vars.palette.Surface.Forward,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "nowrap",
          gap: "8px",
          px: "12px",
          py: "8px",
          overflow: "hidden",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          backgroundColor: theme.vars.palette.Surface.Forward,
        }}
      >
        <Typography
          level="title-sm"
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: "13px",
            fontWeight: 700,
            color: theme.vars.palette.Content.Strong,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          활동지 목록
        </Typography>
        <Button
          size="sm"
          variant="Plain"
          startDecorator={<AddIcon size={12} />}
          onClick={onCreateTask}
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            minHeight: "28px",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          새 활동지
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          py: "4px",
          ...taskEditorScrollbarSx,
        }}
      >
        {sections.length === 0 && (
          <Box sx={{ p: "16px", textAlign: "center" }}>
            <Typography
              level="body-xs"
              sx={{ color: theme.vars.palette.Content.Weak }}
            >
              활동지 없음
            </Typography>
          </Box>
        )}

        {sections.map((section) => {
          const isCollapsed = collapsedSections.has(section.key);
          const firstTaskId = section.tasks[0]?.task_id ?? null;
          return (
            <Box key={section.key} sx={{ mb: "2px" }}>
              {/* 섹션 헤더 — 드롭 시 해당 세트의 맨 위로 */}
              <Box
                onClick={() => toggleSection(section.key)}
                onDragOver={(event) =>
                  handleDragOver(event, {
                    setNumber: section.set_number,
                    beforeTaskId: firstTaskId,
                  })
                }
                onDrop={(event) =>
                  handleDrop(event, {
                    setNumber: section.set_number,
                    beforeTaskId: firstTaskId,
                  })
                }
                sx={{
                  px: "8px",
                  py: "6px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  userSelect: "none",
                  "&:hover": {
                    backgroundColor: theme.vars.palette.Background.Level1,
                  },
                }}
              >
                <Box
                  sx={{
                    ...(isCollapsed && {
                      transform: "rotate(-90deg)",
                      transformOrigin: "center",
                    }),
                    transition: "transform 0.2s",
                  }}
                >
                  <UpArrowSvg
                    color={theme.vars.palette.Content.Weak}
                    size={16}
                  />
                </Box>
                <Typography
                  level="body-xs"
                  sx={{
                    fontWeight: 700,
                    color: theme.vars.palette.Content.Weak,
                    fontSize: "11px",
                    letterSpacing: "0.02em",
                  }}
                >
                  {section.label}
                </Typography>
              </Box>

              {!isCollapsed &&
                section.tasks.map((task, taskIndex) => {
                  const isActive = activeTaskId === task.task_id;
                  const isStreamingTask = streamingSet.has(task.task_id);
                  const isBeingDragged = draggingTaskId === task.task_id;
                  const nextTaskId =
                    section.tasks[taskIndex + 1]?.task_id ?? null;
                  const isLast = taskIndex === section.tasks.length - 1;
                  const showTopLine = isDropLineActive(
                    section.set_number,
                    task.task_id
                  );
                  const showBottomLine =
                    isLast && isDropLineActive(section.set_number, null);

                  return (
                    <Box
                      key={task.task_id}
                      draggable
                      onDragStart={(event) =>
                        handleDragStart(event, task.task_id)
                      }
                      onDragEnd={handleDragEnd}
                      onDragOver={(event) =>
                        handleDragOver(
                          event,
                          buildRowTarget(
                            event,
                            section.set_number,
                            task.task_id,
                            nextTaskId
                          )
                        )
                      }
                      onDrop={(event) =>
                        handleDrop(
                          event,
                          buildRowTarget(
                            event,
                            section.set_number,
                            task.task_id,
                            nextTaskId
                          )
                        )
                      }
                      onClick={() => onSelectTask(task.task_id)}
                      sx={{
                        position: "relative",
                        // 제목 좌측 라인을 섹션 라벨("세트")의 텍스트 시작점과 정렬한다.
                        // 라벨 x = header px(8) + chevron(16) + gap(4) = 28px.
                        // 제목 x = border(2) + pl + slot(18) + gap(6) = 26 + pl → pl 2px.
                        pl: "2px",
                        pr: "4px",
                        py: "4px",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                        opacity: isBeingDragged ? 0.4 : 1,
                        borderLeft: `2px solid ${
                          isActive
                            ? theme.vars.palette.Content.Strong
                            : "transparent"
                        }`,
                        backgroundColor: isActive
                          ? theme.vars.palette.Background.Level2
                          : "transparent",
                        "& .meta-edit-btn": {
                          visibility: "hidden",
                        },
                        "&:hover": {
                          backgroundColor: isActive
                            ? theme.vars.palette.Background.Level2
                            : theme.vars.palette.Background.Level1,
                        },
                        "&:hover .meta-edit-btn": {
                          visibility: "visible",
                        },
                      }}
                    >
                      {/* 드롭 인디케이터 (드래그 중에만) */}
                      {showTopLine && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "-1px",
                            left: "8px",
                            right: "4px",
                            height: "2px",
                            backgroundColor: DROP_LINE_COLOR,
                            borderRadius: "2px",
                            pointerEvents: "none",
                          }}
                        />
                      )}
                      {showBottomLine && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: "-1px",
                            left: "8px",
                            right: "4px",
                            height: "2px",
                            backgroundColor: DROP_LINE_COLOR,
                            borderRadius: "2px",
                            pointerEvents: "none",
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        {/* 인디케이터 슬롯 — 항상 같은 폭을 확보해 제목을 일정하게
                            들여쓰고(스트리밍 토글 시 제목 안 밀림), 슬롯 안 중앙 정렬로
                            좌우 여백을 맞춘다. */}
                        <Box
                          sx={{
                            width: "18px",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {isStreamingTask && <StreamingIndicator />}
                        </Box>
                        <Typography
                          level="body-xs"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "12px",
                            fontWeight: isActive ? 600 : 400,
                            color: theme.vars.palette.Content.Strong,
                          }}
                          title={`${task.title || "(제목 없음)"}`}
                        >
                          {task.title || "(제목 없음)"}
                        </Typography>
                        {task.is_key_activity && (
                          <Typography
                            level="body-xs"
                            sx={{
                              fontSize: "11px",
                              color: KEY_BADGE_COLOR,
                              flexShrink: 0,
                              lineHeight: 1,
                            }}
                            title="Key 활동지"
                          >
                            ★
                          </Typography>
                        )}
                        <Box
                          className="meta-edit-btn"
                          onClick={(event) => {
                            event.stopPropagation();
                            onEditMeta(task.task_id);
                          }}
                          sx={{
                            flexShrink: 0,
                            fontSize: "14px",
                            lineHeight: 1,
                            color: theme.vars.palette.Content.Weak,
                            cursor: "pointer",
                            px: "2px",
                            "&:hover": {
                              color: theme.vars.palette.Content.Strong,
                            },
                          }}
                          title="메타데이터 편집"
                        >
                          <span
                            className="material-symbols-outlined"
                            style={{
                              fontSize: "16px",
                            }}
                          >
                            more_vert
                          </span>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          );
        })}
      </Box>
    </Sheet>
  );
}
